import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Globe, Menu, X, ChevronDown, User, LogOut } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import logo from '../../assets/logo.png';

// Carbon-inspired navbar with brand colors and pharmaceutical professional feel
const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const { i18n, t } = useTranslation();
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const toggleLanguageDropdown = () => {
    setLanguageDropdownOpen(!languageDropdownOpen);
  };

  const handleLanguageChange = (lang: 'fr' | 'en') => {
    i18n.changeLanguage(lang);
    setLanguageDropdownOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getDashboardPath = () => {
    // Check localStorage directly to ensure we always get the correct path
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        const roleId = userData.role_id;
        
        if (roleId === 1) return '/admin/dashboard';
        if (roleId === 2) return '/training-center/dashboard';
        if (roleId === 3) return '/dashboard';
        if (roleId === 4) return '/consultant/dashboard';
      }
    } catch (e) {
    }
    
    // Fallback to context user if localStorage parsing fails
    if (user?.role_id === 1) return '/admin/dashboard';
    if (user?.role_id === 2) return '/training-center/dashboard';
    if (user?.role_id === 3) return '/dashboard';
    if (user?.role_id === 4) return '/consultant/dashboard';
    return '/dashboard';
  };

  const checkIsAuthenticated = () => {
    return !!localStorage.getItem('user');
  };

  const isAuthenticatedFromStorage = checkIsAuthenticated();

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
        scrolled
          ? 'bg-brand-surface shadow-sm border-b border-black/5 backdrop-blur-sm'
          : 'bg-brand-surface/95 backdrop-blur-sm border-b border-black/5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo Section */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center group" onClick={closeMobileMenu}>
              <img src={logo} alt="PHINLEX" className="h-8 w-auto" />
           
            </Link>
          </div>

          {/* Center Navigation - Desktop */}
          <div className="hidden lg:flex items-center space-x-1">
            <Link
              to="/"
              className="px-4 py-2 text-sm font-medium text-gray-900 hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors"
            >
              {t('nav.home')}
            </Link>
            <Link
              to="/trainings"
              className="px-4 py-2 text-sm font-medium text-gray-900 hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors"
            >
              {t('nav.catalog')}
            </Link>
            <Link
              to="/centers"
              className="px-4 py-2 text-sm font-medium text-gray-900 hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors"
            >
              {t('nav.centers')}
            </Link>
            <Link
              to="/consultants"
              className="px-4 py-2 text-sm font-medium text-gray-900 hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors"
            >
              Consultants
            </Link>
         
            {/* <Link
              to="/centres"
              className="px-4 py-2 text-sm font-medium text-gray-900 hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors"
            >
              {t('nav.centers')}
            </Link> */}
          </div>

          {/* Right Actions - Desktop */}
          <div className="hidden md:flex items-center space-x-2">
            {/* Language Dropdown */}
            <div className="relative">
              <button
                onClick={toggleLanguageDropdown}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
              >
                <Globe className="h-4 w-4 mr-2" />
                {i18n.language === 'fr' ? 'FR' : 'EN'}
                <ChevronDown className={`h-4 w-4 ml-1 transition-transform ${languageDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {languageDropdownOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white rounded-md shadow-lg border border-black/5 z-50">
                  <button
                    onClick={() => handleLanguageChange('fr')}
                    className={`block w-full px-4 py-2 text-sm text-left hover:bg-gray-50 transition-colors ${
                      i18n.language === 'fr' ? 'bg-brand-primary/5 text-brand-primary' : 'text-gray-700'
                    }`}
                  >
                    🇫🇷 Français
                  </button>
                  <button
                    onClick={() => handleLanguageChange('en')}
                    className={`block w-full px-4 py-2 text-sm text-left hover:bg-gray-50 transition-colors ${
                      i18n.language === 'en' ? 'bg-brand-primary/5 text-brand-primary' : 'text-gray-700'
                    }`}
                  >
                    🇺🇸 English
                  </button>
                </div>
              )}
            </div>
            
            {(isAuthenticated || isAuthenticatedFromStorage) ? (
              <>
                <Link
                  to={getDashboardPath()}
                  className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors flex items-center gap-2"
                >
                  <User className="h-4 w-4" />
                  {user?.fullName || 'Mon Tableau'}
                </Link>
                <button
                  onClick={handleLogout}
                  className="ml-2 inline-flex h-10 items-center justify-center rounded-md px-3 font-semibold text-red-600 hover:text-red-700 hover:bg-red-50 transition-all"
                  title="Déconnexion"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </>
            ) : (
              <Link
                to="/auth/role-selection"
                className="px-6 py-2 text-sm font-semibold text-brand-primary hover:text-brand-primary/80 transition-all duration-200"
              >
                {t('nav.connection')}
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={toggleMobileMenu}
              className="p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-colors"
              aria-label="Toggle mobile menu"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden transition-all duration-200 ease-in-out overflow-hidden ${
            mobileMenuOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="border-t border-black/5 pt-4 space-y-1">
            <Link
              to="/"
              onClick={closeMobileMenu}
              className="block px-4 py-3 text-base font-medium text-gray-900 hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors"
            >
              {t('nav.home')}
            </Link>
            <Link
              to="/trainings"
              onClick={closeMobileMenu}
              className="block px-4 py-3 text-base font-medium text-gray-900 hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors"
            >
              {t('nav.catalog')}
            </Link>
            <Link
              to="/centers"
              onClick={closeMobileMenu}
              className="block px-4 py-3 text-base font-medium text-gray-900 hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors"
            >
              Centres
            </Link>
            <Link
              to="/consultants"
              onClick={closeMobileMenu}
              className="block px-4 py-3 text-base font-medium text-gray-900 hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors"
            >
              Consultants
            </Link>
            
            {/* Language selector for mobile */}
            <div className="px-4 py-2 border-t border-black/5 mt-2 pt-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Langue</p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => {
                    handleLanguageChange('fr');
                    closeMobileMenu();
                  }}
                  className={`py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                    i18n.language === 'fr' 
                      ? 'bg-brand-primary text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  🇫🇷 Français
                </button>
                <button
                  onClick={() => {
                    handleLanguageChange('en');
                    closeMobileMenu();
                  }}
                  className={`py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                    i18n.language === 'en' 
                      ? 'bg-brand-primary text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  🇺🇸 English
                </button>
              </div>
            </div>
            
            <div className="px-4 py-2 space-y-2 border-t border-black/5 mt-2 pt-4">
              {(isAuthenticated || isAuthenticatedFromStorage) ? (
                <>
                  <Link
                    to={getDashboardPath()}
                    onClick={closeMobileMenu}
                    className="block w-full text-center py-3 px-4 font-medium border border-gray-300 text-gray-900 hover:bg-gray-50 rounded-md transition-colors flex items-center justify-center gap-2"
                  >
                    <User className="h-4 w-4" />
                    {user?.fullName || 'Mon Tableau'}
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      closeMobileMenu();
                    }}
                    className="block w-full text-center py-3 px-4 font-semibold bg-red-600 text-white hover:bg-red-700 rounded-md transition-all flex items-center justify-center gap-2"
                  >
                    <LogOut className="h-4 w-4" />
                    Déconnexion
                  </button>
                </>
              ) : (
                <Link
                  to="/auth/role-selection"
                  onClick={closeMobileMenu}
                  className="block w-full text-center py-3 px-4 font-semibold text-white bg-brand-primary hover:bg-brand-primary/90 rounded-full transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  Connexion
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar; 